import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';

export interface TradingAccount {
    id: string;
    account_number?: number; // Auto-increment account number (e.g., 50000, 50250, 50500)
    customer_id: string;
    customer?: {
        id: string;
        name: string;
        email: string;
    };
    base_currency: string;
    leverage: number;
    balance: number;
    equity: number;
    used_margin: number;
    free_margin: number;
    margin_level: number;
    unrealized_pnl: number;
    daily_pnl: number;
    total_deposits: number;
    total_bonuses: number;
    total_withdrawals: number;
    margin_status: {
        can_trade: boolean;
        margin_call: boolean;
        liquidation_warning: boolean;
    };
    created_at: string;
    updated_at: string;
}

export interface TradingAccountFilters {
    customer_id?: string;
    base_currency?: string;
    margin_status?: string;
    search?: string;
    page?: number;
    per_page?: number;
}

export interface TradingAccountsResponse {
    data: TradingAccount[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
}

// Get all trading accounts with filtering
const fetchTradingAccounts = async (filters?: TradingAccountFilters) => {
    const { data } = await httpClient.get<ApiResponse<TradingAccountsResponse>>(
        API_ENDPOINTS.CRM.TRADING_ACCOUNTS.LIST,
        { params: filters }
    );
    return data.data;
};

// Hook to get trading accounts
export const useTradingAccounts = (filters?: TradingAccountFilters) => {
    return useQuery({
        queryKey: ['crm-trading-accounts', filters],
        queryFn: () => fetchTradingAccounts(filters),
    });
};

// Get trading account by ID
const fetchTradingAccount = async (id: string) => {
    const { data } = await httpClient.get<ApiResponse<TradingAccount>>(
        API_ENDPOINTS.CRM.TRADING_ACCOUNTS.DETAILS(id)
    );
    return data.data;
};

// Hook to get a specific trading account
export const useTradingAccount = (id: string) => {
    return useQuery({
        queryKey: ['crm-trading-account', id],
        queryFn: () => fetchTradingAccount(id),
        enabled: !!id,
    });
};

// Interface for creating a trading account
export interface CreateTradingAccountRequest {
    customer_id: string;
    base_currency: string;
    balance?: number;
    leverage?: number;
}

// Create a trading account
export const useCreateTradingAccount = () => {
    const queryClient = useQueryClient();

    return useMutation<TradingAccount, Error, CreateTradingAccountRequest>({
        mutationFn: async data => {
            const response = await httpClient.post<ApiResponse<TradingAccount>>(
                API_ENDPOINTS.CRM.TRADING_ACCOUNTS.CREATE,
                data
            );
            return response.data.data;
        },
        onSuccess: () => {
            // Invalidate relevant queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['crm-trading-accounts'] });
        },
    });
};

// Interface for updating a trading account
export interface UpdateTradingAccountRequest {
    balance?: number;
    leverage?: number;
    base_currency?: string;
}

// Update a trading account
export const useUpdateTradingAccount = () => {
    const queryClient = useQueryClient();

    return useMutation<TradingAccount, Error, { id: string; data: UpdateTradingAccountRequest }>({
        mutationFn: async ({ id, data }) => {
            const response = await httpClient.put<ApiResponse<TradingAccount>>(
                API_ENDPOINTS.CRM.TRADING_ACCOUNTS.UPDATE(id),
                data
            );
            return response.data.data;
        },
        onSuccess: (_, { id }) => {
            // Invalidate relevant queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['crm-trading-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-trading-account', id] });
        },
    });
};

// Delete a trading account
export const useDeleteTradingAccount = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async id => {
            await httpClient.delete(API_ENDPOINTS.CRM.TRADING_ACCOUNTS.DELETE(id));
        },
        onSuccess: () => {
            // Invalidate relevant queries to refresh data
            queryClient.invalidateQueries({ queryKey: ['crm-trading-accounts'] });
        },
    });
};

// Recalculate a trading account balance from completed transactions
export const useRecalculateTradingAccountBalance = () => {
    const queryClient = useQueryClient();

    return useMutation<TradingAccount, Error, string>({
        mutationFn: async id => {
            const response = await httpClient.post<ApiResponse<TradingAccount>>(
                API_ENDPOINTS.CRM.TRADING_ACCOUNTS.RECALCULATE_BALANCE(id)
            );
            return response.data.data;
        },
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: ['crm-trading-accounts'] });
            queryClient.invalidateQueries({ queryKey: ['crm-trading-account', id] });
            queryClient.invalidateQueries({ queryKey: ['customer-accounts'] });
        },
    });
};
